"use client";
import { buttonVariants, cn } from "@rallly/ui";
import { Button } from "@rallly/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@rallly/ui/dropdown-menu";
import {
  ArrowUpRight,
  LifeBuoyIcon,
  ListIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  Settings2Icon,
  SunIcon,
  SunMoonIcon,
  UserIcon,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { Link } from "@/components/link";
import { OptimizedAvatarImage } from "@/components/optimized-avatar-image";
import { useUser } from "@/features/user/client";
import { Trans, useTranslation } from "@/i18n/client";
import { signOut } from "@/lib/auth-client";
import { useTheme } from "@/lib/theme";

export const UserDropdown = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const router = useRouter();
  const { theme, setTheme } = useTheme();

  if (!user || user.isGuest) {
    return (
      <Link
        href={`/login?redirectTo=${encodeURIComponent("/new")}`}
        className={buttonVariants({ variant: "ghost" })}
      >
        <Trans i18nKey="login" defaults="Login" />
      </Link>
    );
  }

  const { image, name, email } = user;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        data-testid="user-dropdown"
        className={cn("group min-w-0", className)}
        render={
          <Button
            aria-label={t("accountMenu", { defaultValue: "Account menu" })}
            variant="ghost"
            className="rounded-full"
            size="icon"
          />
        }
      >
        <OptimizedAvatarImage src={image} name={name} size="sm" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuLabel className="flex items-center gap-2">
          <OptimizedAvatarImage src={image} name={name} size="md" />
          <div className="grow">
            <div className="font-medium text-foreground text-sm">{name}</div>
            {email ? (
              <div className="font-normal text-muted-foreground text-xs">
                {email}
              </div>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          render={
            <Link
              href="/polls"
              className="flex items-center gap-x-2 sm:hidden"
            />
          }
        >
          <ListIcon className="size-4 text-muted-foreground" />
          <Trans i18nKey="polls" defaults="Polls" />
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            <Link
              href="/settings/profile"
              className="flex items-center gap-x-2"
            />
          }
        >
          <UserIcon className="size-4 text-muted-foreground" />
          <Trans i18nKey="profile" defaults="Profile" />
        </DropdownMenuItem>
        <DropdownMenuItem
          render={
            <Link
              href="/settings/preferences"
              className="flex items-center gap-x-2"
            />
          }
        >
          <Settings2Icon className="size-4 text-muted-foreground" />
          <Trans i18nKey="preferences" defaults="Preferences" />
        </DropdownMenuItem>

        <DropdownMenuItem
          render={
            <Link
              target="_blank"
              href="https://support.kinpal.com"
              className="flex items-center gap-x-2"
            />
          }
        >
          <LifeBuoyIcon className="size-4 text-muted-foreground" />
          <Trans i18nKey="support" defaults="Support" />
          <ArrowUpRight />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <SunMoonIcon />
            <Trans i18nKey="theme" defaults="Theme" />
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
              <DropdownMenuRadioItem value="system">
                <MonitorIcon />
                <Trans i18nKey="themeSystem" defaults="System" />
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="light">
                <SunIcon />
                <Trans i18nKey="themeLight" defaults="Light" />
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <MoonIcon />
                <Trans i18nKey="themeDark" defaults="Dark" />
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            await signOut();
            router.refresh();
          }}
          className="flex items-center gap-x-2"
        >
          <LogOutIcon className="size-4 text-muted-foreground" />
          <Trans i18nKey="logout" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
